import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Phone, Mail, Pencil, Trash2 } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { fmtDate } from "@/lib/status";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [c, setC] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ first_name: "", last_name: "", phone: "", email: "", tax_code: "" });

  const load = () => api.get(`/customers/${id}`).then((r) => setC(r.data)).catch(() => navigate("/customers"));
  useEffect(() => { load(); }, [id]);
  if (!c) return <div className="text-muted-foreground">Caricamento…</div>;

  const openEdit = () => {
    setF({ first_name: c.first_name, last_name: c.last_name, phone: c.phone,
      email: c.email || "", tax_code: c.tax_code || "" });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/customers/${id}`, f);
      toast.success("Dati cliente aggiornati"); setOpen(false); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const delCustomer = async () => {
    if (!window.confirm(`Eliminare il cliente ${c.first_name} ${c.last_name}?`)) return;
    try { await api.delete(`/customers/${id}`); toast.success("Cliente eliminato"); navigate("/customers"); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <button onClick={() => navigate("/customers")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Clienti
      </button>
      <Card className="p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-extrabold">{c.first_name} {c.last_name}</h1>
            <div className="mt-2 flex flex-wrap gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1"><Phone className="h-4 w-4" /> {c.phone}</span>
              {c.email && <span className="flex items-center gap-1"><Mail className="h-4 w-4" /> {c.email}</span>}
              {c.tax_code && <span className="uppercase">{c.tax_code}</span>}
            </div>
          </div>
          {can("customers.manage") && (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" data-testid="edit-customer-btn" onClick={openEdit}>
                <Pencil className="mr-1 h-4 w-4" /> Modifica
              </Button>
              <Button variant="outline" size="sm" data-testid="delete-customer-btn" onClick={delCustomer}
                className="text-red-600 hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950/30">
                <Trash2 className="mr-1 h-4 w-4" /> Elimina
              </Button>
            </div>
          )}
        </div>
      </Card>
      <Card className="overflow-hidden">
        <div className="border-b border-border px-5 py-4 font-heading font-bold">Riparazioni ({c.repairs?.length || 0})</div>
        <div className="divide-y divide-border">
          {(c.repairs || []).map((r) => (
            <button key={r.id} onClick={() => navigate(`/repairs/${r.id}`)}
              className="flex w-full items-center gap-4 px-5 py-3 text-left hover:bg-secondary">
              <span className="font-mono text-sm font-semibold text-primary">{r.repair_number}</span>
              <span className="flex-1">{r.device.brand} {r.device.model}</span>
              <StatusBadge code={r.status_code} />
              <span className="hidden text-xs text-muted-foreground sm:block">{fmtDate(r.received_at)}</span>
            </button>
          ))}
          {(!c.repairs || c.repairs.length === 0) && <div className="px-5 py-10 text-center text-muted-foreground">Nessuna riparazione.</div>}
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifica cliente</DialogTitle>
            <DialogDescription>Aggiorna i dati anagrafici del cliente.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2"><Label>Nome *</Label><Input data-testid="edit-cust-firstname" value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} /></div>
            <div className="space-y-2"><Label>Cognome *</Label><Input data-testid="edit-cust-lastname" value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} /></div>
            <div className="space-y-2"><Label>Telefono *</Label><Input data-testid="edit-cust-phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
            <div className="space-y-2"><Label>Email</Label><Input data-testid="edit-cust-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
            <div className="space-y-2 sm:col-span-2"><Label>Codice fiscale</Label><Input data-testid="edit-cust-taxcode" value={f.tax_code} onChange={(e) => setF({ ...f, tax_code: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button data-testid="save-customer-btn" disabled={saving || !f.first_name || !f.last_name || !f.phone} onClick={save}>{saving ? "Salvataggio…" : "Salva"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
